import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

// redux
import { mapDispatchProps, mapStateToProps } from '../../../../store/store';
import { connect } from 'react-redux';
import Button from '../../../../components/button';
import { ROUTES } from '../../../../routes';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import API from '../../../../api';

import OnAfterModalClosed from '../../../../libraries/onAfterModalClosed';
import UploadableImages from '../../../../libraries/uploadableImages';
import CreateProductMasterModal from '../../../../components/createProductMasterModal/createProductMasterModal';
import Builder from '../builder';
import ProductSelector from '../productSelector';
import Util from '../util';
import { useLanguage } from '@languages/index';

const SNSUploadCeleb = (props: any) => {
  const language = useLanguage();
  const languageTexts = { ...language.common, ...language.pages.sns };
  const [searchParams, setSearchParams] = useSearchParams();
  const celebId = searchParams.get('id');

  const navigate = useNavigate();

  const onAfterModalClosed = new OnAfterModalClosed(props);

  /// state
  const [title, setTitle] = useState('');
  const [products, setProducts] = useState([]);
  const [thumbUplImg, setThumbUplImg] = useState(new UploadableImages(1, []));
  const [detailUplImg, setDetailUplImg] = useState(new UploadableImages(20, []));
  const [disabled, setDisabled] = useState(!!celebId);

  /// query
  // fetch brand
  const fetchBrandRes = useQuery({
    queryKey: ['fetch_brand'],
    queryFn: async () => {
      try {
        const res = await API.Brand.show({ brandToken: props.auth.token });
        props.setBrand(res.data.brand);

        return res;
      } catch (error) {
        props.showModal({
          hideCloseButton: true,
          text: languageTexts.celeb_detail_text_1,
        });
      }
    },
  });

  /// effect
  useEffect(() => {
    if (!celebId) return;
    (async () => {
      try {
        // fetch celeb
        const res = await API.BrandSns.fetchCelebrities({
          brandToken: props.auth.token,
          where: { id: celebId },
        });
        const celeb = res.data.reviews[0];

        // set states
        setTitle(celeb.title);
        setProducts(
          celeb.products.map((e) => {
            e.id = e.productId;
            delete e.celebrityId;
            delete e.productId;
            e.imageUrl = e.thumbUrl;
            return e;
          }),
        );
        setThumbUplImg(
          new UploadableImages(1, [
            {
              url: Util.makeImageUrl('brand_sns/thumb-sm', celeb.thumbnailUrl),
              imgUrl: celeb.thumbnailUrl,
            },
          ]),
        );
        setDetailUplImg(
          new UploadableImages(
            20,
            celeb.images.map((e) => {
              e.url = Util.makeImageUrl('brand_sns/thumb-sm', e.imgUrl);
              return e;
            }),
          ),
        );
        setDisabled(false);
      } catch (error) {
        console.log(error);
        props.showModal({
          hideCloseButton: true,
          text: languageTexts.celeb_detail_text_2,
        });
      }
    })();
  }, []);

  /// others
  thumbUplImg.callUseThings();
  detailUplImg.callUseThings();

  const _showProductSelectorModal = (initSearchTxt: string = '') => {
    props.showModal({
      isVisible: true,
      hideCloseButton: true,
      content: () => {
        return (
          <ProductSelector
            initSearchTxt={initSearchTxt}
            onSelect={(prd) => {
              const hasAlready = products.filter((p) => p.id === prd.id).length > 0;
              if (hasAlready === false) setProducts(products.concat(prd));
            }}
            searchProducts={true}
          />
        );
      },
    });
  };

  /// sns install check
  if (
    fetchBrandRes.data &&
    fetchBrandRes.data.brand &&
    fetchBrandRes.data.brand.hasExperiencedSnsInstall &&
    !fetchBrandRes.data.brand.snsPassInstalledAt
  ) {
    navigate(ROUTES.SNS_PAYMENT_COMPLETE.PATH);
  }

  /// build products
  const buildProducts = () => {
    return products.map((prd, idx) => (
      <div
        key={prd.id}
        className="my-[10px] w-full flex flex-row justify-items-start items-center"
        style={{ position: 'relative' }}
      >
        <img
          className="object-contain w-[72px] h-[72px] bg-[#D9D9D9]"
          src={Util.makeImageUrl('product/thumb-sm', prd.imageUrl)}
        />
        <div className="flex flex-col gap-1 w-full overflow-hidden px-5">
          <span className="font-medium text-[15px] whitespace-nowrap overflow-hidden overflow-ellipsis">
            {prd.name}
          </span>

          <span className="text-black text-[15px] font-normal" style={{ wordBreak: 'keep-all' }}>
            {languageTexts._wear(prd.sizeName)}
          </span>

          <div className="flex flex-row gap-5 justify-between">
            <a
              className="cursor-pointer text-[15px] text-[#0066FF] underline whitespace-nowrap truncate text-ellipsis overflow-hidden max-w-[350px]"
              onClick={() => {
                Util.onLinkClick(prd.url);
              }}
            >
              {`${Util.getUrlText(prd.url)}`}
            </a>
          </div>
        </div>

        <div className="cursor-pointer">
          <img
            style={{ position: 'absolute', top: '0px', right: '0px' }}
            src="/assets/icons/ic-close-black-squared.svg"
            onClick={(e) => {
              const newProducts = [];
              for (let pi = 0; pi < products.length; ++pi) {
                if (pi !== idx) newProducts.push(products[pi]);
              }
              setProducts(newProducts);
            }}
          />
        </div>
      </div>
    ));
  };

  /// build main
  const build = () => {
    if (!fetchBrandRes.data) return null;

    if (!props.brand?.hasExperiencedSnsInstall || !props.brand?.remainSnsPass) return null;

    return (
      <div className="flex flex-col items-center w-[1025px] mx-auto my-[50px]">
        <div className="flex flex-row w-full">
          {Builder.buildBackwardText((e) => {
            navigate(ROUTES.SNS_INDEX.PATH);
          })}
        </div>

        <div style={{ height: '39px' }} />

        <span className="font-bold text-[22px] text-[#000000]">
          {languageTexts.celeb_order_text_1} {celebId ? languageTexts.modify2 : languageTexts.celeb_upload_text_1}
        </span>

        <div style={{ height: '70px' }} />

        <div className="flex flex-col items-center w-[335px]">
          {Builder.buildTitle(languageTexts.title)}
          <div style={{ height: '16px' }} />
          {Builder.buildTextInput(languageTexts.title, [title, setTitle], disabled)}

          <div style={{ height: '46px' }} />

          {Builder.buildTitle(languageTexts.celeb_upload_text_2)}
          <div style={{ height: '32px' }} />
          {thumbUplImg.build(languageTexts.celeb_upload_text_2, disabled)}

          <div style={{ height: '46px' }} />

          {Builder.buildTitle(languageTexts.celeb_upload_text_3)}
          <div style={{ height: '32px' }} />
          {detailUplImg.build(languageTexts.celeb_upload_text_3, disabled)}

          <div style={{ height: '46px' }} />

          <div className="flex flex-row w-full">
            {Builder.buildTitle(languageTexts.celeb_upload_text_4)}
            <span className="grow" />
            <span
              className="cursor-pointer underline text-[13px] text-[#000000]"
              onClick={(e) => {
                if (disabled) return;

                _showProductSelectorModal();
              }}
            >
              {languageTexts.celeb_upload_text_5}
            </span>
          </div>
          <div style={{ height: '10px' }} />
          {buildProducts()}
          <div style={{ height: '10px' }} />
          <Button
            bordered
            borderColor={'#E8E8E8'}
            text={languageTexts.uploader_text_6}
            onClick={() => {
              if (disabled) return;

              props.showModal({
                isVisible: true,
                preventCloseWithBackgroundDimmer: true,
                content: () => {
                  return (
                    <CreateProductMasterModal
                      method="CREATE"
                      onSuccess={(data) => {
                        if (data.productMaster) {
                          setTimeout(() => {
                            onAfterModalClosed.set(() => _showProductSelectorModal(data.productMaster.name));
                          }, 500);
                        }
                      }}
                    />
                  );
                },
              });
            }}
          />

          <div style={{ height: '50px' }} />

          <Button
            text={languageTexts.celeb_upload_text_6}
            buttonColor={'BLACK'}
            borderColor={'BLACK'}
            textColor={'WHITE'}
            textClassName={'prose text-sm'}
            onClick={async () => {
              if (disabled) return;

              try {
                /// validate
                const validateErros = [];
                if (title.trim().length <= 0) validateErros.push(languageTexts.celeb_upload_text_7);
                if (products.length <= 0) validateErros.push(languageTexts.celeb_upload_text_8);
                if (thumbUplImg.size() <= 0) validateErros.push(languageTexts.celeb_upload_text_9);
                if (detailUplImg.size() <= 0) validateErros.push(languageTexts.celeb_upload_text_10);
                if (validateErros.length > 0) {
                  return props.showModal({
                    hideCloseButton: true,
                    text: validateErros.join('\n'),
                  });
                }

                /// block screen
                props.showModal({
                  hideCloseButton: true,
                  hideConfirmButton: true,
                  text: languageTexts.celeb_upload_text_11,
                });

                /// upload images
                const uploadedThumbImgUrls = await thumbUplImg.upload(props.auth.token);
                const uploadedDetailImgUrls = await detailUplImg.upload(props.auth.token);

                /// create or update BrandSnsCelebrity
                try {
                  if (!celebId) {
                    // create
                    const res = await API.BrandSns.createCelebrity({
                      brandToken: props.auth.token,
                      title: title,
                      thumbnail_url: uploadedThumbImgUrls[0],
                      image_urls: uploadedDetailImgUrls.filter((e) => !!e),
                      product_ids: products.map((p) => p.id),
                    });
                  } else {
                    // update
                    const res = await API.BrandSns.updateCelebrity(celebId, {
                      brandToken: props.auth.token,
                      title: title,
                      thumbnail_url: uploadedThumbImgUrls[0]
                        ? uploadedThumbImgUrls[0]
                        : thumbUplImg.getImgInfo(0).userInfo.imgUrl,
                      image_urls: uploadedDetailImgUrls.map((e, ei) =>
                        !!e ? e : detailUplImg.getImgInfo(ei).userInfo.imgUrl,
                      ),
                      product_ids: products.map((p) => p.id),
                    });
                  }
                } catch (error) {
                  props.showModal({
                    hideCloseButton: true,
                    text: languageTexts.celeb_upload_text_12,
                  });
                  throw error;
                }

                /// show finish modal
                onAfterModalClosed.set(() => {
                  props.showModal({
                    hideCloseButton: true,
                    text: languageTexts.celeb_upload_text_13,
                    confirm: (params) => {
                      navigate(ROUTES.SNS_INDEX.PATH);
                    },
                  });
                });
                props.closeModal(); // close block-screen-modal
              } catch (e) {
                // show err message
                props.showModal({
                  hideCloseButton: true,
                  text: languageTexts.celeb_upload_text_14,
                });
              }

              // unblock screen
            }}
          />
        </div>
      </div>
    );
  };

  return build();
};

export default connect(mapStateToProps, mapDispatchProps)(SNSUploadCeleb);
